import { z } from 'zod';

/**
 * The wire contract. Only the envelope and the auth payloads live here —
 * per-screen shapes belong next to their screen.
 */

/* ------------------------------------------------------------- envelope */
/**
 * Every 2xx JSON body under /api is `{success, message, data}`, produced by
 * src/envelope.py. `message` is required, never null: on a failure it is the
 * sentence the API wrote for the operator.
 */
export const ApiEnvelopeSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.unknown(),
  meta: z.record(z.string(), z.unknown()).optional(),
});

export type ApiEnvelope = z.infer<typeof ApiEnvelopeSchema>;

/** Narrows `success` to true for an endpoint whose data shape is known. */
export const createApiEnvelopeSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    message: z.string(),
    data: dataSchema,
    meta: z.record(z.string(), z.unknown()).optional(),
  });

/* ----------------------------------------------------------------- auth */
/**
 * `id` is a string because the users table's primary key is a bigint and JS
 * cannot hold it exactly; src/auth.py casts it. `apiToken` is the shared
 * per-user token from that same row — optional here because the column is
 * nullable, unlike in the project this schema came from.
 */
export const AuthUserSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  email: z.string().email(),
  apiToken: z.string().optional().default(''),
  permissions: z.array(z.string()).optional().default([]),
});

export const AuthLoginDataSchema = z.object({
  token: z.string().min(1),
  user: AuthUserSchema,
});

export type UserInfo = z.infer<typeof AuthUserSchema>;
export type AuthLoginResponse = z.infer<typeof AuthLoginDataSchema>;

/* ----------------------------------------------------------- validation */
export function validateResponse<T>(schema: z.ZodType<T>, value: unknown): T {
  const result = schema.safeParse(value);
  if (result.success) return result.data;
  const first = result.error.issues[0];
  const path = first?.path.join('.') || '(root)';
  throw new Error(`Invalid API response: ${path}: ${first?.message ?? 'unknown'}`);
}
